<template>
  <el-form
    ref="formRef"
    :model="formData"
    :rules="validationRules"
    :label-width="labelWidth"
    :label-position="labelPosition"
    :autocomplete="autocomplete ? 'on' : 'off'"
  >
    <el-form-item
      v-for="field in fields"
      :key="field.model"
      :label="field.label"
      :prop="field.model"
    >
      <component
        :is="getComponentType(field.type)"
        v-if="field.type !== 'checkbox'"
        v-model="formData[field.model]"
        :name="formData[field.name ?? '']"
        :placeholder="field.placeholder"
        :autocomplete="autocomplete ? 'on' : 'off'"
        value-format="YYYY-MM-DD"
        v-bind="field.props"
      >
        <template v-if="field.type === 'select'">
          <el-option
            v-for="option in field.options"
            :key="option?.value"
            :label="option?.label"
            :value="option?.value"
          />
        </template>
      </component>
      <el-checkbox
        v-else
        v-model="formData[field.model]"
      >
        {{ field.label }}
      </el-checkbox>
    </el-form-item>
  </el-form>
</template>

<script setup lang="ts">
import {ref, onMounted, watch, isRef, isReactive} from 'vue'
import type {FormConfigField} from '@/types/Form'
import type {FormInstance, FormRules} from 'element-plus'
import type {FormValidateCallback} from 'element-plus/es/components/form/src/types'

const props = defineProps<{
  fields: FormConfigField[]
  labelWidth?: string
  labelPosition?: string
  autocomplete?: boolean
}>()

const formRef = ref<FormInstance>()
const formData = ref<{ [key: string]: boolean | string | number }>({})

const initializeValidationRules = () => {
  const rules: FormRules<typeof formData.value> = {}
  props.fields.forEach((field) => {
    if (field.validation) {
      rules[field.model] = field.validation
    }
  })
  return rules
}
const validationRules = ref<FormRules<typeof formData.value>>(initializeValidationRules())
const initializeForm = () => {
  props.fields.forEach((field) => {
    if (typeof field.value !== 'undefined' && field.value !== null) {
      formData.value[field.model] = field.value
    } else {
      formData.value[field.model] = field.type === 'checkbox' ? false : ''
    }
  })
}
const getComponentType = (type: string) => {
  switch (type) {
    case 'input':
      return 'el-input'
    case 'select':
      return 'el-select'
    case 'checkbox':
      return 'el-checkbox'
    case 'date':
      return 'el-date-picker'
    default:
      return 'el-input'
  }
}

onMounted(() => {
  initializeForm()
})

if (isRef(props.fields) || isReactive(props.fields)) {
  watch(props.fields, () => {
    initializeValidationRules()
    initializeForm()
  })
}

const validateForm = async (callback?: FormValidateCallback) => {
  return formRef?.value?.validate(callback)
}

const clearForm = () => {
  if (formRef.value) {
    formRef.value.resetFields()
  }
}

const getFormData = () => {
  const setValueType = (item: FormConfigField) => {
    if (item.dataType === 'array') {
      return [formData.value[item.model]]
    }

    if (item.props?.type === 'number') {
      return Number(formData.value[item.model])
    }

    return formData.value[item.model]
  }

  return props.fields.reduce(
    (acc, item) => {
      if (item.additionalAttr) {
        return {
          ...acc,
          attributes: [
            ...acc.attributes,
            {
              name: item.model,
              value: setValueType(item),
            },
          ],
        }
      }

      return {
        ...acc,
        [item.model]: setValueType(item),
      }
    },
    {attributes: []} as any,
  )
}

defineExpose({
  validateForm,
  clearForm,
  formData,
  getFormData,
})
</script>

<style scoped></style>
